/**
 * Flat `sent | mark` contract stream (TT-955) → display structures.
 *
 * The wire contract has no segment container: it is one ordered list of
 * `sent` items (anchored marks riding in `sent.marks`) and free-floating
 * `mark` items (window/meeting-scoped signals). Consumers regroup it into
 * speaker turns purely for display — the regroup is a view model, not part
 * of the contract. This is the single shared implementation (TT-903 render
 * convergence); hosts must not hand-roll their own copy.
 */

/**
 * Regroup the flat stream into display turns (runs of same-speaker `sent`
 * items). Each sentence carries its derived paint (from its `symbolizer`
 * mark: `display_score` + `symbol_ids`) plus all of its anchored `marks`, and
 * its place on the doc's audio (`t0` / `t1`) when the stream carries one.
 * Each turn also keeps `items` — the original stream order of its `sent`s
 * and any free-floating `mark`s — so transcripts can render window marks
 * inline at their true position instead of lumped at the end of a turn.
 */
export function streamToSegments(stream) {
  const segments = [];
  let cur = null;
  for (const it of stream || []) {
    if (it?.kind === 'sent') {
      const sym = (it.marks || []).find((m) => m.annotator === 'symbolizer');
      const sentence = {
        idx: it.idx,
        text: it.text,
        display_score: sym ? sym.score : 0,
        symbol_ids: sym ? sym.symbol_ids : [],
        marks: it.marks || [],
      };
      // The sentence's place on the doc's audio, when the read gave one
      // (seconds from the audio's start; a player's media clock, TT-1230).
      if (Number.isFinite(it.t0)) sentence.t0 = it.t0;
      if (Number.isFinite(it.t1)) sentence.t1 = it.t1;
      const speaker = it.speaker ?? null;
      if (!cur || cur.speaker !== speaker) {
        cur = { seqnum: segments.length, speaker, start: it.start ?? null, sentences: [], items: [] };
        segments.push(cur);
      }
      cur.sentences.push(sentence);
      cur.items.push({ kind: 'sent', sentence });
    } else if (it?.kind === 'mark') {
      if (!cur) {
        cur = { seqnum: segments.length, speaker: null, sentences: [], items: [] };
        segments.push(cur);
      }
      cur.items.push({ kind: 'mark', mark: it });
    }
  }
  return segments;
}

/**
 * A display turn's sentences joined into one run of prose — for transcript views
 * that read a turn as a paragraph rather than sentence by sentence. Sentences are
 * objects on the scored contract but plain strings on unscored corpus segments,
 * so both are accepted.
 */
export function segmentText(segment) {
  return (segment?.sentences || []).map(sentenceText).filter(Boolean).join(' ');
}

/** One sentence's text, whichever shape it arrived in (see segmentText). */
export function sentenceText(sentence) {
  return typeof sentence === 'string' ? sentence : sentence?.text || '';
}

/**
 * A display turn's stable identity, for list keys. Turns regrouped here carry
 * `seqnum`, but segments arriving straight off a service may be camelCased, and
 * an unscored corpus segment may carry neither — hence the ordinal fallback.
 */
export function segmentSeq(segment, fallback) {
  return segment?.seqnum ?? segment?.seqNum ?? fallback;
}

/**
 * Count the `sent` items in a flat stream — the honest progress unit at
 * per-utterance cadence, where turn counts freeze on single-speaker meetings
 * (TT-942 live finding 1).
 */
export function streamSentenceCount(stream) {
  let n = 0;
  for (const it of stream || []) {
    if (it?.kind === 'sent') n += 1;
  }
  return n;
}

/**
 * Count the sentences contained in `segments[0..uptoIdx]` (regrouped display
 * turns). Omit `uptoIdx` for the total across all turns.
 */
export function segmentsSentenceCount(segments, uptoIdx) {
  const last = (segments?.length || 0) - 1;
  const upto = uptoIdx == null ? last : Math.min(uptoIdx, last);
  let n = 0;
  for (let i = 0; i <= upto; i++) {
    n += segments[i]?.sentences?.length || 0;
  }
  return n;
}

/**
 * Turn → sentence: the playhead position at the END of display turn `turnIdx`.
 *
 * This is the conversion every turn-stepping host needs, because the renderer's
 * playhead (`currentSent`) is a SENTENCE — a live meeting grows inside its last
 * turn, so a turn index can't name a position in one (TT-971).
 *
 * Turn-stepping suits replaying a FINISHED meeting, where the turns are settled.
 * Don't reach for it to drive a live transport: a turn is a run of one speaker,
 * so it spans anywhere from a single sentence to the whole meeting. Measured on
 * live transcripts, one turn-step moved 3–19% of a meeting, and a single-speaker
 * meeting is one turn — nowhere to step at all. Live transports want a fixed
 * stride in sentences (tapp's LiveTapPage).
 *
 * Deliberately defined out of range, so callers can step off either end without
 * clamping first: a negative `turnIdx` gives -1 (render nothing) and one past
 * the end gives the meeting's last sentence.
 */
export function turnEndSentence(segments, turnIdx) {
  return segmentsSentenceCount(segments, turnIdx) - 1;
}

/**
 * Sentence → turn: the display turn containing sentence ordinal `sent`. The
 * inverse of `turnEndSentence`, for keeping turn-scoped chrome (a transcript
 * highlight, a current-speaker readout) in step with a sentence playhead.
 * Clamps to the last turn; returns 0 for an empty playhead or no segments.
 */
export function turnAtSentence(segments, sent) {
  const count = segments?.length || 0;
  let n = 0;
  for (let i = 0; i < count; i++) {
    n += segments[i]?.sentences?.length || 0;
    if (sent < n) return i;
  }
  return Math.max(count - 1, 0);
}
